import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { OverlapAnswer, OverlapQuestion } from './EditSession';

/** Square metres as a person reads them: m² below a hectare, ha above. */
export function areaText(m2: number, locale: string): string {
  if (m2 >= 10_000) return `${(m2 / 10_000).toLocaleString(locale, { maximumFractionDigits: 2 })} ha`;
  return `${m2.toLocaleString(locale, { maximumFractionDigits: m2 < 10 ? 2 : 0 })} m²`;
}

/**
 * A polygon lies over its neighbours: the shared parts are red on the map, what is left after clipping dashed
 * green. The person chooses (the owner's decision): clip it to the neighbours, save it anyway, or cancel.
 * Not modal over the map, so the red can be seen; placed at the bottom, above the toolbar.
 */
export const OverlapDialog: React.FC<{ question: OverlapQuestion; locale: string; onAnswer: (a: OverlapAnswer) => void }> =
  ({ question, locale, onAnswer }) => {
    const { t } = useTranslation();
    return (
      <div className="fixed left-1/2 -translate-x-1/2 bottom-28 z-[70] w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-gn-danger bg-gn-modal shadow-gn-lg"
           role="alertdialog" aria-modal="false" data-testid="editing-overlap">
        <div className="flex items-start gap-2 px-3 py-2">
          <AlertTriangle size={18} className="text-gn-danger shrink-0 mt-0.5" />
          <div className="text-xs text-gn-primary space-y-1">
            <p className="font-medium">{t('editing.overlap.title', { count: question.overlaps.length })}</p>
            <p className="text-gn-secondary">{t('editing.overlap.body', { area: areaText(question.overlapArea, locale) })}</p>
            <p className="text-[10px] text-gn-muted">{t('editing.overlap.legend')}</p>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-3 pb-2">
          <button type="button" className="px-2.5 py-1 text-xs rounded border border-gn-light text-gn-secondary hover:bg-gn-hover"
                  onClick={() => onAnswer('cancel')} data-testid="overlap-cancel">
            {t('editing.cancel')}
          </button>
          <button type="button" className="px-2.5 py-1 text-xs rounded border border-gn-warning text-gn-warning hover:bg-gn-warning-light"
                  onClick={() => onAnswer('allow')} data-testid="overlap-allow">
            {t('editing.overlap.allow')}
          </button>
          <button type="button" className="px-2.5 py-1 text-xs rounded bg-gn-blue text-gn-inverse disabled:opacity-40"
                  disabled={!question.clipped || question.clipEmpty} onClick={() => onAnswer('clip')} data-testid="overlap-clip"
                  title={question.clipEmpty ? t('editing.errors.clip_empty') : undefined} autoFocus>
            {t('editing.overlap.clip')}
          </button>
        </div>
      </div>
    );
  };

export default OverlapDialog;
